/**
 * Chat Assistant — REST surface.
 *
 * Integration guide §3. This is the *legacy* ticket issuer, and the guide is
 * explicit that it should be ignored for the token-based WebSocket flow:
 *
 *   "Only needed for the old `?ticket=` connection flow. Ignore for token-based
 *    WebSocket integration."   — §3
 *   "Do not call the legacy ticket endpoint for the token-based flow."  — §14
 *
 * It is implemented here only so an environment still running the old server
 * flow can be pointed at it with `REACT_APP_CHAT_WS_AUTH_MODE=ticket`. Nothing
 * calls it in the default configuration, and the client never falls back to it
 * on its own — a silent downgrade from the current flow to a legacy one is
 * exactly the kind of thing that should require a deliberate switch.
 */

import { api } from "core/axios/axios";

/** Shapes seen from ticket issuers; the field name is not pinned by the guide. */
interface TicketResponse {
    ticket?: string;
    Ticket?: string;
    token?: string;
    data?: { ticket?: string; token?: string };
}

/**
 * POST `/chat-assistant-ticket` and return the one-time ticket.
 *
 * Goes through the shared `api` instance, so it carries the Cognito **ID** token
 * in the `authorizationToken` header like every other REST call in this app.
 * That is intentionally different from the WebSocket, which uses the **access**
 * token (§1) — the two transports authenticate differently.
 */
export async function requestChatAssistantTicket(): Promise<string> {
    const { data } = await api.post<TicketResponse>("chat-assistant-ticket");

    const ticket =
        data?.ticket ?? data?.Ticket ?? data?.token ?? data?.data?.ticket ?? data?.data?.token;

    if (!ticket || typeof ticket !== "string") {
        throw new Error("Ticket issuer returned no ticket.");
    }
    return ticket;
}
